import { useEffect, useState } from 'react';
import type { ReactGanttRef } from '@dhx/react-gantt';

export type GanttTargetsVisibleRow = {
  id: string;
  /** True when DHTMLX reports children (same signal as the tree expander). */
  isNonLeaf: boolean;
};

type GanttLike = {
  eachTask?: (cb: (task: { id: string | number; parent?: string | number; type?: string; $open?: boolean }) => void) => void;
  getTask?: (id: string | number) => { id?: string | number; parent?: string | number; $open?: boolean; type?: string } | null;
  hasChild?: (id: string | number) => boolean;
  getChildren?: (id: string | number) => Array<string | number>;
  getVisibleTaskCount?: () => number;
  getTaskByIndex?: (index: number) => { id?: string | number; type?: string } | null;
  isTaskVisible?: (id: string | number) => boolean;
  config?: { root_id?: string | number };
  attachEvent?: (name: string, cb: (...args: any[]) => void) => string;
  detachEvent?: (id: string) => void;
};

function isRootParent(gantt: GanttLike, parentId: unknown): boolean {
  if (parentId == null || parentId === '' || parentId === 0 || parentId === '0') return true;
  const rootId = gantt.config?.root_id;
  if (rootId != null && String(parentId) === String(rootId)) return true;
  return false;
}

/**
 * Cached ancestor-open check — O(depth) once per node, reused across siblings.
 */
function isTaskShownInTree(
  gantt: GanttLike,
  taskId: string | number,
  shownCache: Map<string, boolean>,
): boolean {
  const key = String(taskId);
  const cached = shownCache.get(key);
  if (cached != null) return cached;

  if (!gantt.getTask) {
    shownCache.set(key, true);
    return true;
  }

  const task = gantt.getTask(taskId);
  if (!task) {
    shownCache.set(key, true);
    return true;
  }

  const parentId = task.parent;
  if (isRootParent(gantt, parentId)) {
    shownCache.set(key, true);
    return true;
  }

  const parentShown = isTaskShownInTree(gantt, parentId as string | number, shownCache);
  if (!parentShown) {
    shownCache.set(key, false);
    return false;
  }

  const parent = gantt.getTask(parentId as string | number);
  const shown = parentShown && parent?.$open !== false;
  shownCache.set(key, shown);
  return shown;
}

function taskHasChildren(gantt: GanttLike, taskId: string | number): boolean {
  if (typeof gantt.hasChild === 'function' && gantt.hasChild(taskId)) return true;
  if (typeof gantt.getChildren === 'function') {
    const kids = gantt.getChildren(taskId);
    if (Array.isArray(kids) && kids.length > 0) return true;
  }
  return false;
}

/**
 * Read visible row order + leaf/non-leaf from the live DHTMLX instance.
 * Prefers getTaskByIndex so onBeforeTaskDisplay filters (look-ahead focus) match
 * the left grid and Targets panel 1:1 for scroll sync.
 */
export function collectVisibleRowsFromGantt(gantt: GanttLike | null | undefined): GanttTargetsVisibleRow[] {
  if (!gantt) return [];

  if (typeof gantt.getVisibleTaskCount === 'function' && typeof gantt.getTaskByIndex === 'function') {
    const n = gantt.getVisibleTaskCount();
    const rows: GanttTargetsVisibleRow[] = [];
    for (let i = 0; i < n; i += 1) {
      const task = gantt.getTaskByIndex(i);
      if (task == null || task.id == null) continue;
      const id = String(task.id);
      const isNonLeaf = taskHasChildren(gantt, task.id) || task.type === 'project';
      rows.push({ id, isNonLeaf });
    }
    return rows;
  }

  if (!gantt.eachTask) return [];
  const rows: GanttTargetsVisibleRow[] = [];
  const shownCache = new Map<string, boolean>();
  gantt.eachTask((task) => {
    if (typeof gantt.isTaskVisible === 'function' && !gantt.isTaskVisible(task.id)) return;
    if (!isTaskShownInTree(gantt, task.id, shownCache)) return;
    const id = String(task.id);
    const isNonLeaf = taskHasChildren(gantt, task.id) || task.type === 'project';
    rows.push({ id, isNonLeaf });
  });
  return rows;
}

function rowsEqual(a: GanttTargetsVisibleRow[], b: GanttTargetsVisibleRow[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i]!.id !== b[i]!.id || a[i]!.isNonLeaf !== b[i]!.isNonLeaf) return false;
  }
  return true;
}

/**
 * Keeps Targets panel rows synced to the rendered Gantt tree
 * (open/close, parse, data render, view mode, look-ahead filter).
 */
export function useGanttTargetsVisibleRows(
  ganttRef: React.RefObject<ReactGanttRef | null>,
  enabled: boolean,
  /** Bump when React task data / collapse / filter changes so we re-read the instance. */
  syncKey: string | number,
): GanttTargetsVisibleRow[] {
  const [rows, setRows] = useState<GanttTargetsVisibleRow[]>([]);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      return;
    }

    let cancelled = false;
    let tries = 0;
    let retryTimer: number | undefined;
    let rafId = 0;
    let debounceTimer: number | undefined;
    const eventIds: string[] = [];

    const syncNow = () => {
      if (cancelled) return;
      const gantt = ganttRef.current?.instance as GanttLike | undefined;
      if (!gantt?.eachTask && !gantt?.getTaskByIndex) {
        tries += 1;
        if (tries < 40) retryTimer = window.setTimeout(syncNow, 50);
        return;
      }
      const next = collectVisibleRowsFromGantt(gantt);
      setRows((prev) => (rowsEqual(prev, next) ? prev : next));
    };

    /** Coalesce bursty gantt render events — critical at 10k+ tasks. */
    const scheduleSync = () => {
      if (cancelled) return;
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = window.requestAnimationFrame(syncNow);
      }, 32);
    };

    const attach = () => {
      const gantt = ganttRef.current?.instance as GanttLike | undefined;
      if (!gantt?.attachEvent) {
        tries += 1;
        if (tries < 40) retryTimer = window.setTimeout(attach, 50);
        else syncNow();
        return;
      }
      for (const name of [
        'onDataRender',
        'onParse',
        'onTaskOpened',
        'onTaskClosed',
        'onGanttRender',
        'onAfterTaskAdd',
        'onAfterTaskDelete',
      ]) {
        const id = gantt.attachEvent?.(name, () => {
          scheduleSync();
        });
        if (id) eventIds.push(id);
      }
      syncNow();
      window.setTimeout(syncNow, 60);
      window.setTimeout(syncNow, 200);
    };

    attach();

    return () => {
      cancelled = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
      if (rafId) window.cancelAnimationFrame(rafId);
      const gantt = ganttRef.current?.instance as GanttLike | undefined;
      for (const id of eventIds) gantt?.detachEvent?.(id);
    };
  }, [ganttRef, enabled, syncKey]);

  return rows;
}
